// @unocss-include

import { GRAPHQL_INT_32_MAX } from '@/constants/graphql';
import { NODE_KIND } from '@/constants/node';
import {
  AddSystemNodesToProjectMutation,
  Exact,
  FetchProjectNodesByIdsQueryVariables,
  FindFileImportRequestsByDataImportRequestIdQueryVariables,
  FindFolderImportRequestDataQueryVariables,
  FolderImportRequestStatusEnum,
  InputMaybe,
  ListSystemNodesByParentNodeIdQueryVariables,
  ListSystemNodesBySystemNodeIdsQueryVariables,
  QueryCountNumberOfNodesForParentNodesArgs,
  Scalars,
  SystemNodeKindInput,
} from '@/graphql/codegen/graphql';
import {
  COUNT_NUMBER_OF_NODES_FOR_PARENT_NODES,
  FETCH_PROJECT_NODES_BY_IDS,
  FIND_FILE_IMPORT_REQUESTS_BY_DATA_IMPORT_REQUEST_ID,
  FIND_FOLDER_IMPORT_REQUEST_BY_DATA_IMPORT_REQUEST_ID,
  FIND_PROJECT_BY_PROJECT_ID,
  FIND_SYSTEM_NODES_BY_SYSTEM_NODE_IDS,
  LIST_SYSTEM_NODES_BY_PARENT_NODE_ID,
} from '@/graphql/queries';
import { request } from '@/graphql/request';
import { SystemNode } from '@/stores/systemNodes';
import { BreadthFirstTreeProcessor } from '@skand/utils';
import { PaginatedProcessor } from '@skand/utils';
import { createSystemNode } from '@/utils/transformers';
import { FileTree, FileTreeNode, FolderNode } from '@skand/uploader';
import { UseMutationResult } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import chunk from 'lodash-es/chunk';
import { basename, extname } from 'path-browserify';
import { useCreateProjectNodeFiles, useCreateProjectNodeFolder } from './mutations';

export function assertIsString(examinee: unknown): asserts examinee is string {
  if (typeof examinee !== 'string') throw new Error('examinee is not a string');
}

export const formatBytes = (bytes: number | null | undefined, decimals = 2) => {
  if (!bytes || bytes === 0)
    return {
      amount: 0,
      unit: 'Bytes',
    };

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const indexOfUnit = Math.floor(Math.log(bytes) / Math.log(k));

  return {
    amount: (bytes / k ** indexOfUnit).toFixed(dm),
    unit: units[indexOfUnit],
  };
};

const displayError = (error: Error) => {
  if ('message' in error) return error.message;
  return error;
};
export const displayErrors = (errors: unknown) => {
  if (errors instanceof Array) return displayError(errors[0]);
  else return displayError(errors as Error);
};

const CHUNK_THRESHOLD = 500;
export const createProjectNodesFromFileTreeNode = async (
  fileTree: FileTree,
  folderNode: FolderNode,
  projectId: string,
  parentNodeId: null | string,
  createProjectNodeFolder: ReturnType<typeof useCreateProjectNodeFolder>,
  createProjectNodeFiles: ReturnType<typeof useCreateProjectNodeFiles>,
) => {
  for (const childFolderPath of folderNode.childFolderAbsolutePaths) {
    const childFolderNode = fileTree.getNode(childFolderPath);
    if (!childFolderNode) continue;
    if (childFolderNode.type !== 'folder') continue;

    const { createProjectFolderNode: childFolderProjectNode } = await createProjectNodeFolder({
      projectId,
      parentNodeId,
      name: basename(childFolderPath),
    });

    const childFolderProjectNodeId = childFolderProjectNode?.id;
    if (!childFolderProjectNodeId) continue;
    await createProjectNodesFromFileTreeNode(
      fileTree,
      childFolderNode,
      projectId,
      childFolderProjectNodeId,
      createProjectNodeFolder,
      createProjectNodeFiles,
    );
  }

  const childFileSystemNodeIds: string[] = [];
  for (const childFilePath of folderNode.childFileAbsolutePaths) {
    const childFileNode = fileTree.getNode(childFilePath);
    if (!childFileNode) continue;
    if (childFileNode.type !== 'file') continue;
    if (childFileNode.fileSystemNodeId === null) continue;
    childFileSystemNodeIds.push(childFileNode.fileSystemNodeId);
  }

  const chunkedChildFileSystemNodeIds = chunk(childFileSystemNodeIds, CHUNK_THRESHOLD);
  for (const fileSystemNodeIds of chunkedChildFileSystemNodeIds) {
    await createProjectNodeFiles({ projectId, parentNodeId, nodeIds: fileSystemNodeIds });
  }
};

export const fetchSystemNodesByParentNodeId = async (
  variables: ListSystemNodesByParentNodeIdQueryVariables,
) => {
  const result = await request(LIST_SYSTEM_NODES_BY_PARENT_NODE_ID, variables);
  return result;
};

export const fetchSystemNodesByIds = async (
  variables: ListSystemNodesBySystemNodeIdsQueryVariables,
) => {
  const result = await request(FIND_SYSTEM_NODES_BY_SYSTEM_NODE_IDS, variables);
  return result.findSystemNodesByIds;
};

export const countChildOfFolders = async ({
  parentNodeIds,
}: QueryCountNumberOfNodesForParentNodesArgs): Promise<number> => {
  const result = await request(COUNT_NUMBER_OF_NODES_FOR_PARENT_NODES, { parentNodeIds });
  return result.countNumberOfNodesForParentNodes ?? 0;
};

export const findProjectByProjectId = async (projectId: string) => {
  const result = await request(FIND_PROJECT_BY_PROJECT_ID, { projectId });
  return result.readProjectById;
};

export const fetchProjectNodesByIds = async (variables: FetchProjectNodesByIdsQueryVariables) => {
  const result = await request(FETCH_PROJECT_NODES_BY_IDS, variables);
  return result.projectNodesByIds;
};

export const findFileImportRequestsByDataImportRequestId = async (
  variables: FindFileImportRequestsByDataImportRequestIdQueryVariables,
) => {
  const result = await request(FIND_FILE_IMPORT_REQUESTS_BY_DATA_IMPORT_REQUEST_ID, variables);
  return result.findFileImportRequestsByDataImportRequestId;
};

export const findFolderImportRequestData = async (
  variables: FindFolderImportRequestDataQueryVariables,
) => {
  const result = await request(FIND_FOLDER_IMPORT_REQUEST_BY_DATA_IMPORT_REQUEST_ID, variables);
  return result.findFolderImportRequestData;
};

export const getInitialNodes = async (
  dataImportRequestId: string,
  rootFolderImportRequestId: string,
) => {
  const fileImportRequests = await findFileImportRequestsByDataImportRequestId({
    dataImportRequestId,
  });
  const folderImportRequests = await findFolderImportRequestData({
    filter: {
      dataImportRequestId,
      statuses: [FolderImportRequestStatusEnum.FileNodesImported],
      limit: GRAPHQL_INT_32_MAX,
      skip: 0,
    },
  });

  const fileImportRequestsAssociatedToRootFolderImportRequest = fileImportRequests?.filter(
    fileImportRequest => fileImportRequest?.folderImportRequestId === rootFolderImportRequestId,
  );
  const folderImportRequestsAssociatedToRootFolderImportRequest = folderImportRequests?.filter(
    folderImportRequest =>
      folderImportRequest?.parentFolderImportRequestId === rootFolderImportRequestId,
  );

  const systemFolderNodeIds = folderImportRequestsAssociatedToRootFolderImportRequest?.map(
    folderImportRequest => folderImportRequest?.systemFolderNodeId,
  );
  const systemFileNodeIds = fileImportRequestsAssociatedToRootFolderImportRequest?.map(
    fileImportRequest => fileImportRequest?.systemFileNodeId,
  );

  const systemNodeIds = [...(systemFolderNodeIds ?? []), ...(systemFileNodeIds ?? [])].filter(
    id => !!id,
  );

  const systemNodes = await fetchSystemNodesByIds({
    systemNodeIds: systemNodeIds as string[],
  });

  return systemNodes;
};

export const addSystemNodesToProject = async (
  dataImportRequestId: string,
  rootFolderImportRequestId: string,
  projectId: string,
  projectParentNodeId: string | null,
  addSystemNodesToProjectMutation: UseMutationResult<
    AddSystemNodesToProjectMutation,
    unknown,
    Exact<{
      systemNodeIds: Array<InputMaybe<Scalars['ID']['input']>> | InputMaybe<Scalars['ID']['input']>;
      projectId: Scalars['ID']['input'];
      systemParentNodeId: Scalars['ID']['input'];
    }>,
    unknown
  >,
) => {
  assertIsString(projectId);

  const BATCH_SIZE = 500;
  const MAX_PARALLEL_BATCHES = 3;
  const CHILD_FETCH_PAGE_SIZE = 500;

  const nodeIdToCreatedSystemNodeIds = new Map();

  const processor = new BreadthFirstTreeProcessor({
    batchSize: BATCH_SIZE,
    maxParallelBatches: MAX_PARALLEL_BATCHES,
    getChildren: async (node: SystemNode) => {
      if (node.kind !== NODE_KIND.FOLDER_NODE) {
        return [];
      }

      const paginatedProcessor = new PaginatedProcessor<number, SystemNode[]>({
        pageSize: CHILD_FETCH_PAGE_SIZE,
        maxParallelPages: MAX_PARALLEL_BATCHES,
        getTotal: async () => {
          return await countChildOfFolders({ parentNodeIds: [node.id] });
        },
        processPage: async (_, pageIndex) => {
          const result = await fetchSystemNodesByParentNodeId({
            parentNodeId: node.id,
            searchTerm: null,
            pageIndex,
            nodeKinds: [SystemNodeKindInput.FileNode, SystemNodeKindInput.FolderNode],
            pageSize: CHILD_FETCH_PAGE_SIZE,
            hydrateFiles: false,
          });

          const nodes: SystemNode[] = [];
          const nodeDataList = result?.listSystemNodesByParentNodeId?.data ?? [];

          nodeDataList.forEach(nodeData => {
            if (!isEmpty(nodeData)) {
              const systemNode = createSystemNode(nodeData);
              if (systemNode) {
                nodes.push(systemNode);
              }
            }
          });

          return nodes;
        },
      });

      const allPagesData = await paginatedProcessor.processAllPages(pageIndex => pageIndex);
      return allPagesData.flat();
    },
  });

  const initialNodes = await getInitialNodes(dataImportRequestId, rootFolderImportRequestId);

  if ((initialNodes?.length ?? 0) > 0) {
    await processor.processSiblingBatchInLevel(
      initialNodes as unknown as SystemNode[],
      async (siblingBatch, parent, levelIndex) => {
        const systemNodeBatchIdsToAdd = siblingBatch.map(node => node.id);

        let systemParentNodeIdToUse = null;
        if (levelIndex === 0) {
          const shouldUseProjectUserFilesRootFolderNodeId = projectParentNodeId === null;

          if (shouldUseProjectUserFilesRootFolderNodeId) {
            const fetchProjectResult = await findProjectByProjectId(projectId);

            systemParentNodeIdToUse = fetchProjectResult?.userFilesRootFolderNodeId;
          } else {
            const fetchProjectParentNodeResult = await fetchProjectNodesByIds({
              projectNodeIds: [projectParentNodeId],
            });

            systemParentNodeIdToUse = fetchProjectParentNodeResult?.[0]?.tags?.find(
              t => t?.key === 'migrationSystemNodeId',
            )?.value;
          }
        } else {
          systemParentNodeIdToUse = nodeIdToCreatedSystemNodeIds.get(parent?.id);
        }

        if (!systemParentNodeIdToUse) {
          throw new Error(
            `Error adding files to project: System parent node ID not found for level index ${levelIndex}`,
          );
        }

        const addFilesResult = await addSystemNodesToProjectMutation.mutateAsync({
          systemNodeIds: systemNodeBatchIdsToAdd,
          projectId,
          systemParentNodeId: systemParentNodeIdToUse,
        });

        // Store the mapping between original node IDs and the newly created node IDs
        const addedNodes = addFilesResult?.addSystemNodesToProject ?? [];

        if (addedNodes.length > 0) {
          addedNodes.forEach(mapping => {
            if (mapping && mapping.originalNodeId && mapping.createdNodeId) {
              nodeIdToCreatedSystemNodeIds.set(mapping.originalNodeId, mapping.createdNodeId);
            }
          });
        }
      },
    );
  }
};

export const EXTENSION_TO_ICON_CLASS: Record<string, string> = {
  DXF: 'color-neutral-500 text-4 i-skand-dxf',
  IFC: 'color-neutral-500 text-4 i-skand-ifc',
  KML: 'color-neutral-500 text-4 i-skand-bim',
  LAS: 'color-neutral-500 text-4 i-skand-pointcloud',
  LAZ: 'color-neutral-500 text-4 i-skand-pointcloud',
  MTL: 'color-neutral-500 text-4 i-skand-obj',
  OBJ: 'color-neutral-500 text-4 i-skand-obj',
  TIF: 'color-neutral-500 text-4 i-skand-ortho',
  TIFF: 'color-neutral-500 text-4 i-skand-ortho',

  DOCX: 'color-neutral-500 text-4 i-custom:docx',
  DWG: 'color-neutral-500 text-4 i-custom:dwg',
  E57: 'color-neutral-500 text-4 i-custom:e57', // or i-skand-pointcloud
  FXL: 'color-neutral-500 text-4 i-custom:fxl',
  LISCAD: 'color-neutral-500 text-4 i-custom:liscad',
  MJF: 'color-neutral-500 text-4 i-custom:mjf',
  PDF: 'color-neutral-500 text-4 i-custom:pdf',
  PNG: 'color-neutral-500 text-4 i-custom:png',
  PPTX: 'color-neutral-500 text-4 i-custom:pptx',
  RCP: 'color-neutral-500 text-4 i-custom:rcp',
  RVT: 'color-neutral-500 text-4 i-custom:rvt',
  XLSX: 'color-neutral-500 text-4 i-custom:xlsx',
  ZIP: 'color-neutral-500 text-4  i-custom:zip', // or i-skand-mesh3d
};

export const getFileTreeNodeIconClass = (fileTreeNode: FileTreeNode) => {
  if (fileTreeNode.type === 'folder') return 'i-skand-files color-neutral-500 text-4';

  const { file } = fileTreeNode;
  if (file.type.includes('video')) return 'i-custom:videos color-neutral-500 text-4';
  if (file.type.includes('image')) return 'i-skand-image color-neutral-500 text-4';

  const extension = extname(file.name).slice(1).toUpperCase();
  const iconClass = EXTENSION_TO_ICON_CLASS[extension];
  if (iconClass) return iconClass;

  return 'i-skand-file color-neutral-500 text-4';
};
